
import { useState, FormEvent } from 'react';

export default function NewsletterSignup() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d6j41hefjdhm2lq4t2sg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setIsSuccess(true);
        setEmail('');
        setTimeout(() => setIsSuccess(false), 5000);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative py-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-50 via-teal-50 to-amber-100"></div>
      
      {/* Decorative Cat Paw Prints */}
      <div className="absolute top-8 left-8 text-6xl opacity-10 animate-pulse">🐾</div>
      <div className="absolute bottom-12 right-12 text-7xl opacity-10 animate-pulse delay-300">🐾</div>
      <div className="absolute top-1/2 right-1/4 text-5xl opacity-10 animate-pulse delay-500">🐾</div>
      
      <div className="relative max-w-4xl mx-auto text-center">
        {/* Headline */}
        <div className="mb-6">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
            Get Paw-Picked Deals in Your Inbox <span className="inline-block animate-bounce">🐾</span>
          </h2>
          <p className="text-base sm:text-lg text-gray-700 max-w-2xl mx-auto">
            Join thousands of cat lovers getting exclusive product picks, discounts, and cat care tips delivered weekly
          </p>
        </div>

        {/* Form */}
        <form 
          id="newsletter-signup-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="max-w-xl mx-auto mb-8"
        >
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              disabled={isSubmitting || isSuccess}
              className="flex-1 px-5 py-4 text-base sm:text-lg rounded-full border-2 border-gray-300 focus:border-teal-500 focus:ring-4 focus:ring-teal-100 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              required
            />
            <button
              type="submit"
              disabled={isSubmitting || isSuccess}
              className="px-8 py-4 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-bold text-base sm:text-lg rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none whitespace-nowrap min-h-[56px] flex items-center justify-center"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Joining...
                </span>
              ) : isSuccess ? (
                <span className="flex items-center gap-2">
                  <i className="ri-check-line"></i>
                  Subscribed!
                </span>
              ) : (
                'Join the Clowder'
              )}
            </button>
          </div>
          
          {error && (
            <p className="mt-3 text-red-600 text-sm font-medium">{error}</p>
          )}
        </form>

        {/* Success Message */}
        {isSuccess && (
          <div className="mb-8 p-4 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border-2 border-teal-200 animate-[slideUp_0.5s_ease-out]">
            <div className="flex items-center justify-center gap-3 text-teal-700">
              <span className="text-4xl animate-bounce">🐾</span>
              <p className="text-lg font-bold">Welcome to the clowder! Check your inbox for a confirmation email.</p>
              <span className="text-4xl animate-bounce delay-150">🐾</span>
            </div>
          </div>
        )}

        {/* Value Props */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 max-w-3xl mx-auto">
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-5 shadow-md hover:shadow-lg transition-shadow">
            <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-3 shadow-md">
              <i className="ri-trophy-line text-2xl text-white"></i>
            </div>
            <h3 className="font-bold text-gray-900 mb-1 text-base sm:text-lg">Weekly Pick of the Week</h3>
            <p className="text-sm text-gray-600">Curated product recommendations every Monday</p>
          </div>

          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-5 shadow-md hover:shadow-lg transition-shadow">
            <div className="w-14 h-14 bg-gradient-to-br from-teal-400 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-3 shadow-md">
              <i className="ri-price-tag-3-line text-2xl text-white"></i>
            </div>
            <h3 className="font-bold text-gray-900 mb-1 text-base sm:text-lg">Exclusive Discount Codes</h3>
            <p className="text-sm text-gray-600">Subscriber-only deals on top cat products</p>
          </div>

          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-5 shadow-md hover:shadow-lg transition-shadow">
            <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-3 shadow-md">
              <i className="ri-notification-badge-line text-2xl text-white"></i>
            </div>
            <h3 className="font-bold text-gray-900 mb-1 text-base sm:text-lg">New Product Alerts</h3>
            <p className="text-sm text-gray-600">Be first to know about trending cat gear</p>
          </div>
        </div>

        {/* Privacy Note */}
        <p className="mt-6 text-xs sm:text-sm text-gray-500">
          We respect your inbox. Unsubscribe anytime. No spam, just purrs. 🐱
        </p>
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .delay-150 {
          animation-delay: 150ms;
        }
        .delay-300 {
          animation-delay: 300ms;
        }
        .delay-500 {
          animation-delay: 500ms;
        }
      `}</style>
    </section>
  );
}
